import {IAiChatPanel} from '@/interface';
import {getMorphoService, serviceNames} from '@/rte/editor';
import * as store from '@/store';
import {useCallback, useEffect} from 'react';
import eventBus, {EDITOR_FS_DOM_READY} from '@/eventBus';

import {EDITOR_MODE} from '@/Constant';
import {set as localStorageSet, get as localStorageGet} from '@/utils/storageUtils';
import {aiChatPanelShow} from '@/utils/document/aigc';
import {SideTabKey} from '@/interface';
import {handleCommentEvent, initPanelStatus, resetPanelStatus} from '../utils/panelStatus';

const defaultData = {
    isExpanded: false,
    selectedTab: SideTabKey.Comment,
    isExternalNotComment: false,
    isAiAssistant: true, // 帅哥@xieshuai02之前接助手sdk小流量加的字段，全量后直接写死true。
};

// 同步展开收起状态
export const useSyncExpand: (prop: {
    curDocument: WizDocument;
    aiChatPanel?: IAiChatPanel;
}) => void = ({aiChatPanel, curDocument}) => {
    const commentService = getMorphoService(serviceNames.ICommentService);
    useEffect(() => {
        if (commentService && aiChatPanel && aiChatPanel.docGuid === curDocument.docGuid) {
            const curExpandState = commentService.state.showCommentList.getValue();
            if (curExpandState !== aiChatPanel.isExpanded) { // 避免重复更新状态，避免触发额外的subscribe
                commentService.state.showCommentList.next(aiChatPanel.isExpanded);
            }
        }
    }, [commentService, aiChatPanel, curDocument.docGuid]);

    let sub: any = null;
    const initSubscribe = useCallback(() => {
        const commentService = getMorphoService(serviceNames.ICommentService);

        if (commentService) {
            // 取消上一次的监听
            if (sub) {
                sub.unsubscribe();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
            sub = commentService.state.showCommentList.subscribe({
                next(value: boolean) {
                    // 进入演示模式、历史版本模式会触发这个事件，这里不处理
                    if (store.getData(store.KEYS.DEMO_MODE) || store.getDocumentMode() === EDITOR_MODE.history) {
                        return;
                    }
                    handleCommentEvent(value);
                },
            });
        }
    }, []);

    useEffect(() => {
        eventBus.on(EDITOR_FS_DOM_READY, initSubscribe);

        return () => {
            eventBus.off(EDITOR_FS_DOM_READY, initSubscribe);
        };
    }, [initSubscribe]);
};

// 记录缓存&初始化状态
export const useInitExpandData: (prop: {
    curDocument: WizDocument;
    aiChatPanel?: IAiChatPanel;
    documentMode?: EDITOR_MODE;
}) => void = ({curDocument, aiChatPanel, documentMode}) => {
    // 记录数据
    useEffect(() => {
        // 排除空数据和其他文档数据
        if (!aiChatPanel || aiChatPanel.docGuid !== curDocument.docGuid) {
            return;
        }

        const localDataMap = localStorageGet(aiChatPanelShow) || {};
        // 面板收起，移除数据
        // 面板展开，更新数据
        if (aiChatPanel.isExpanded === false) {
            delete localDataMap[curDocument.docGuid];
            localStorageSet(aiChatPanelShow, localDataMap);
        } else {
            const localData = localDataMap[curDocument.docGuid];
            const hasUpdate =
                localData?.selectedTab !== aiChatPanel.selectedTab || localData?.isExpanded !== aiChatPanel.isExpanded;
            if (hasUpdate) {
                localStorageSet(aiChatPanelShow, {
                    ...(localDataMap || {}),
                    [curDocument.docGuid]: aiChatPanel,
                });
            }
        }
    }, [aiChatPanel, curDocument]);

    // 切换文档初始化
    useEffect(() => {
        const localDataMap = localStorageGet(aiChatPanelShow) || {};
        const aiChatPanel = localDataMap[curDocument.docGuid] || defaultData;
        initPanelStatus(aiChatPanel, curDocument.docGuid);
    }, [curDocument.docGuid]);

    // 切换文档模式时，标记选中的ai tab（不然会被评论覆盖）
    useEffect(() => {
        const aiChatPanel = store.getAiChatPanel();
        if (aiChatPanel.docGuid === curDocument.docGuid) {
            initPanelStatus(aiChatPanel, curDocument.docGuid);
        }
    }, [documentMode, curDocument.docGuid]);
};

// 组件销毁后重置store状态
export const useResetStoreData = () => {
    useEffect(() => {
        return () => {
            resetPanelStatus(defaultData);
        };
    }, []);
};
